import React, { useContext, useEffect, useState } from "react";
import { WebsiteContext } from "../../context/WebsiteProvider";
import TeamSearch from "../../components/inputs/TeamSearch";
import { FiTrash2, FiPlus } from "react-icons/fi";
import DeleteModal from '../../components/modals/DeleteModal';
import SuccessNotification from '../../components/toast/SuccessNotification';
import ErrorNotification from '../../components/toast/ErrorNotification';

const WebsiteTeam = ({ websiteId, reciprocalAllowed }) => {
  const {
    team,
    setTeam,
    getWebsiteTeam,
    createWebsiteTeam,
    deleteWebsiteTeam,
    updateWebsiteTeam,
  } = useContext(WebsiteContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [agentIdToDelete, setAgentIdToDelete] = useState(null);
  const [showDeleteTeamModal, setShowDeleteTeamModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [text, setText] = useState('');
  const token = localStorage.getItem("token");

  const hasFetched = React.useRef(false);

  useEffect(() => {
    const fetchTeam = async () => {
      if (!hasFetched.current && websiteId) {
        hasFetched.current = true;
        try {
          const data = await getWebsiteTeam(websiteId, token);
          setTeam(data || { id: 1, siteId: websiteId, primary: null, memberIds: [], members: [], teamCount: 0 });
        } catch (error) {
          setShowError(true);
          setText(`Failed to fetch team: ${error.message}`);
        }
      }
    };
    fetchTeam();
  }, [websiteId, token, getWebsiteTeam, setTeam]);

  const handleAgentSelect = (agent) => {
    setSelectedAgent({
      firstName: agent.firstName || "",
      lastName: agent.lastName || "",
      mlsId: agent.mlsId || "",
      memberNumber: agent.memberNumber || "",
      office: agent.office || "",
      status: agent.status || "",
    });
  };

  const handleAddAgent = async () => {
    if (!selectedAgent) {
      setShowError(true);
      setText("Please select an agent to add");
      return;
    }

    const memberNumberInt = parseInt(selectedAgent.memberNumber, 10);
    if (!memberNumberInt || team.memberIds.includes(memberNumberInt)) {
      setShowError(true);
      setText("Invalid or duplicate member number");
      return;
    }

    const updatedMemberIds = [...team.memberIds, memberNumberInt];
    const updatedMembers = [...team.members, selectedAgent];
    const teamData = {
      id: team.id || 1,
      siteId: websiteId,
      primary: team.primary || memberNumberInt,
      memberIds: updatedMemberIds,
      members: updatedMembers,
      teamCount: updatedMembers.length,
    };

    try {
      const endpoint = team.memberIds.length === 0 ? createWebsiteTeam : updateWebsiteTeam;
      const result = await endpoint(websiteId, teamData, token);
      if (result) {
        setTeam(teamData);
        resetForm();
        setShowSuccess(true);
        setText('Team member added successfully');
      }
    } catch (error) {
      setShowError(true);
      setText(`Failed to add team member: ${error.message}`);
    }
  };

  const handleRemoveAgent = (memberId) => {
    if (!memberId) {
      setShowError(true);
      setText('Cannot remove: Member ID is missing');
      return;
    }
    setAgentIdToDelete(memberId);
    setShowDeleteModal(true);
  };

  const handleRemoveConfirmation = async () => {
    try {
      const updatedMemberIds = team.memberIds.filter((id) => id !== parseInt(agentIdToDelete, 10));
      const updatedMembers = team.members.filter((member) => parseInt(member.memberNumber, 10) !== parseInt(agentIdToDelete, 10));

      const updatedTeamData = {
        ...team,
        memberIds: updatedMemberIds,
        members: updatedMembers,
        primary: updatedMemberIds.includes(team.primary) ? team.primary : updatedMemberIds[0] || null,
        teamCount: updatedMembers.length,
      };

      const result = await updateWebsiteTeam(websiteId, updatedTeamData, token);
      if (result) {
        setTeam(updatedTeamData);
        setShowSuccess(true);
        setText('Team member removed successfully');
      }
    } catch (error) {
      setShowError(true);
      setText(`Failed to remove team member: ${error.message}`);
    } finally {
      setShowDeleteModal(false);
    }
  };

  const handleDeleteTeam = () => {
    setShowDeleteTeamModal(true);
  };

  const handleDeleteTeamConfirmation = async () => {
    try {
      await deleteWebsiteTeam(websiteId, token);
      setTeam({ id: null, siteId: websiteId, primary: null, memberIds: [], members: [], teamCount: 0 });
      setShowSuccess(true);
      setText('Team deleted successfully');
    } catch (error) {
      setShowError(true);
      setText(`Failed to delete team: ${error.message}`);
    } finally {
      setShowDeleteTeamModal(false);
    }
  };

  const resetForm = () => {
    setIsModalOpen(false);
    setSelectedAgent(null);
  };

  const closeNotification = () => {
    setShowSuccess(false);
    setShowError(false);
  };

  return (
    <div className="rounded-lg bg-white p-4 dark:bg-stone-900">
      <div className="flex space-x-4 mb-4">
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center justify-center rounded-md bg-blue-500 px-6 py-2 text-white hover:bg-blue-700 dark:bg-indigo-500 dark:hover:bg-indigo-700"
        >
          <FiPlus className="mr-2" />
          Add Team Member
        </button>
        {team.members?.length > 0 && (
          <button
            onClick={handleDeleteTeam}
            className="flex items-center justify-center rounded-md bg-red-500 px-6 py-2 text-white hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700"
          >
            <FiTrash2 className="mr-2" />
            Delete Team
          </button>
        )}
      </div>

      {team.members?.length > 0 ? (
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-left text-sm text-gray-500 dark:divide-stone-700 dark:text-gray-300">
            <thead className="sticky top-0 bg-white text-xs uppercase text-gray-700 dark:bg-stone-900 dark:text-gray-300">
            <tr>
              <th className="py-3 pl-6">First Name</th>
              <th className="px-6 py-3">Last Name</th>
              <th className="px-6 py-3">MLS ID</th>
              <th className="px-6 py-3">Member Number</th>
              <th className="px-6 py-3">Office</th>
              <th className="px-6 py-3">Status</th>
              <th className="px-6 py-3">Primary</th>
              <th className="px-6 py-3">Actions</th>
            </tr>
            </thead>
            <tbody>
            {team.members.map((agent) => (
              <tr
                key={agent.memberNumber}
                className="border-b bg-white hover:bg-gray-50 dark:border-stone-700 dark:bg-stone-800 dark:hover:bg-stone-600"
              >
                <td className="px-6 py-2.5">{agent.firstName}</td>
                <td className="px-6 py-2.5">{agent.lastName}</td>
                <td className="px-6 py-2.5">{agent.mlsId}</td>
                <td className="px-6 py-2.5">{agent.memberNumber}</td>
                <td className="px-6 py-2.5">{agent.office}</td>
                <td className="px-6 py-2.5">{agent.status}</td>
                <td className="px-6 py-2.5">
                  {parseInt(agent.memberNumber, 10) === team.primary ? "Yes" : ""}
                </td>
                <td className="px-6 py-2.5 min-h-[60px] flex items-center">
                  {parseInt(agent.memberNumber, 10) !== team.primary ? (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleRemoveAgent(agent.memberNumber)}
                        className="flex items-center rounded px-2 py-2 font-semibold text-red-500 hover:text-red-400"
                      >
                        <FiTrash2 className="mr-2" />
                        Remove
                      </button>
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="mt-4 text-gray-500 dark:text-gray-300">No team members found.</p>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md dark:bg-stone-900">
            <h2 className="text-lg font-semibold mb-4 dark:text-gray-300">
              Add Team Member
            </h2>
            <TeamSearch
              onAgentSelect={handleAgentSelect}
              reciprocalAllowed={reciprocalAllowed}
              darkMode={false}
            />
            {selectedAgent && (
              <div className="mt-4 space-y-2 text-gray-700 dark:text-gray-300">
                <p><strong>First Name:</strong> {selectedAgent.firstName}</p>
                <p><strong>Last Name:</strong> {selectedAgent.lastName}</p>
                <p><strong>MLS ID:</strong> {selectedAgent.mlsId}</p>
                <p><strong>Member Number:</strong> {selectedAgent.memberNumber}</p>
                <p><strong>Office:</strong> {selectedAgent.office}</p>
                <p><strong>Status:</strong> {selectedAgent.status}</p>
              </div>
            )}
            <div className="mt-6 flex justify-end space-x-2">
              <button
                onClick={resetForm}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 dark:bg-stone-700 dark:hover:bg-stone-600 dark:text-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleAddAgent}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-700 dark:bg-indigo-500 dark:hover:bg-indigo-700"
                disabled={!selectedAgent}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <DeleteModal
          title="Are you sure you want to remove this team member?"
          onCancel={() => setShowDeleteModal(false)}
          onDelete={handleRemoveConfirmation}
        />
      )}

      {showDeleteTeamModal && (
        <DeleteModal
          title="Are you sure you want to delete the entire team?"
          onCancel={() => setShowDeleteTeamModal(false)}
          onDelete={handleDeleteTeamConfirmation}
        />
      )}

      {showSuccess && (
        <SuccessNotification
          show={showSuccess}
          onClose={closeNotification}
          text={text}
        />
      )}
      {showError && (
        <ErrorNotification
          show={showError}
          onClose={closeNotification}
          text={text}
        />
      )}
    </div>
  );
};

export default WebsiteTeam;